import { apiClient } from '@/lib/api/client'
import type { TaskFormFieldDefinition } from '@/components/helpdesk/TicketDetail/tasks/modules/types'
import type { TaskTemplate } from '@/lib/services/childTicketService'

type ApiResponse<T> = { success: boolean; data?: T; message?: string; meta?: { total?: number } }

export type TaskTemplateStepInput = {
  id?: number
  temp_id?: string
  title: string
  status_code: string
  action_type?: string
  form_fields?: TaskFormFieldDefinition[]
  default_sla_minutes?: number | null
  assigned_team_id?: number | null
  sort_order?: number
  depends_on_step_id?: number | null
  checklist_template_id?: number | null
}

export type TaskTemplateChecklistItemInput = {
  id?: number
  label: string
  sort_order?: number
  required?: boolean
}

export type TaskTemplateRule = Record<string, unknown> | string | null

export type TaskTemplateInput = {
  name: string
  code?: string | null
  description?: string | null
  title_pattern?: string | null
  notes_pattern?: string | null
  default_assignee_rule?: TaskTemplateRule
  default_priority_rule?: TaskTemplateRule
  due_date_rule?: TaskTemplateRule
  allowed_creator_roles?: string[] | null
  task_field_module_code?: string | null
  default_status_code?: string | null
  support_type_id?: number | null
  is_sequential?: boolean
  is_active?: boolean
  checklist_template_id?: number | null
  steps?: TaskTemplateStepInput[]
  checklist_items?: TaskTemplateChecklistItemInput[]
}

export type TaskTemplateDefaults = {
  template_code?: string | null
  template_id?: number
  template_name?: string
  title?: string
  notes?: string | null
  status?: string
  action_type?: string
  priority?: string
  assigned_to?: number | null
  assigned_team_id?: number | null
  due_at?: string | null
  sla_minutes_override?: number | null
  form_fields?: TaskFormFieldDefinition[]
  form_data?: Record<string, unknown>
}

export type CreateTaskFromTemplatePayload = {
  template_code: string
  context?: {
    part_ids?: number[]
    document_purpose?: string
    source_field?: string
  }
}

export const taskTemplateService = {
  async list(params?: { search?: string; per_page?: number }): Promise<{ items: TaskTemplate[]; total: number }> {
    const res = await apiClient.get<ApiResponse<TaskTemplate[]>>('/service-catalog/task-templates', { params })
    const data = res.data?.data ?? res.data
    return {
      items: Array.isArray(data) ? data : [],
      total: res.data?.meta?.total ?? (Array.isArray(data) ? data.length : 0),
    }
  },

  async get(id: number): Promise<TaskTemplate> {
    const res = await apiClient.get<ApiResponse<TaskTemplate>>(`/service-catalog/task-templates/${id}`)
    if (!res.data?.data) throw new Error(res.data?.message ?? 'Template not found')
    return res.data.data
  },

  async create(payload: TaskTemplateInput): Promise<TaskTemplate> {
    const res = await apiClient.post<ApiResponse<TaskTemplate>>('/service-catalog/task-templates', payload)
    if (!res.data?.data) throw new Error(res.data?.message ?? 'Failed to create template')
    return res.data.data
  },

  async update(id: number, payload: TaskTemplateInput): Promise<TaskTemplate> {
    const res = await apiClient.put<ApiResponse<TaskTemplate>>(`/service-catalog/task-templates/${id}`, payload)
    if (!res.data?.data) throw new Error(res.data?.message ?? 'Failed to update template')
    return res.data.data
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/service-catalog/task-templates/${id}`)
  },

  async getDefaults(code: string, parentTicketId: string | number, context?: CreateTaskFromTemplatePayload['context']): Promise<TaskTemplateDefaults> {
    const res = await apiClient.get<ApiResponse<TaskTemplateDefaults>>(`/service-catalog/task-templates/${code}/defaults`, {
      params: {
        parentTicketId,
        context,
      },
    })
    if (!res.data?.data) throw new Error(res.data?.message ?? 'Failed to resolve template defaults')
    return res.data.data
  },

  async createTaskFromTemplate(
    parentTicketId: string | number,
    payload: CreateTaskFromTemplatePayload
  ): Promise<import('@/lib/services/childTicketService').ChildTicketTask> {
    const res = await apiClient.post<ApiResponse<import('@/lib/services/childTicketService').ChildTicketTask>>(
      `/parent-tickets/${parentTicketId}/tasks`,
      payload
    )
    if (!res.data?.data) throw new Error(res.data?.message ?? 'Failed to create task from template')
    return res.data.data
  },
}
